import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "tulocalmx_admin_panel_key";

const AdminProAlertas = () => {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [alerts, setAlerts] = useState([]);
  const [rows, setRows] = useState([]);
  const [advisorLogs, setAdvisorLogs] = useState([]);
  const [advisorStats, setAdvisorStats] = useState({
    total: 0,
    averageScore: 0,
    hardRuleCases: 0,
    topBusinesses: [],
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingSignals, setIsPreparingSignals] = useState(false);
  const [signalMessage, setSignalMessage] = useState("");

  const loadAlerts = async () => {
    if (!key.trim()) {
      setError("Escribe tu clave admin.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      localStorage.setItem(STORAGE_KEY, key.trim());

      const response = await fetch(
        `/api/admin-pro-alerts?key=${encodeURIComponent(key.trim())}`
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo cargar.");
      }

      setAlerts(data.alerts || []);
      setRows(data.rows || []);
      setAdvisorLogs(data.advisorLogs || []);
      setAdvisorStats(data.advisorStats || {});
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareExistingProperties = async () => {
    if (!key.trim()) {
      setError("Escribe tu clave admin.");
      return;
    }

    try {
      setIsPreparingSignals(true);
      setError("");
      setSignalMessage("");
      const response = await fetch("/api/admin-pro-alerts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-key": key.trim(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo preparar.");
      }

      setSignalMessage(
        `Propiedades preparadas: ${data.updated} de ${data.total}. Errores: ${data.failed}.`
      );
    } catch (prepareError) {
      setError(prepareError.message);
    } finally {
      setIsPreparingSignals(false);
    }
  };

  useEffect(() => {
    if (key) loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8">
      <section className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-orange-600">
              Administracion Pro
            </p>
            <h1 className="mt-1 text-2xl font-black text-slate-950">
              Alertas de analisis
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Revisa publicaciones Pro donde el analisis esta pendiente o fallo.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
            aria-label="Salir"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row">
          <input
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder="Clave admin"
            className="min-w-0 flex-1 rounded-lg border border-slate-200 px-4 py-3 outline-none focus:border-orange-400"
          />
          <button
            type="button"
            onClick={loadAlerts}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-3 text-sm font-black text-white disabled:bg-orange-300"
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-800">
            {error}
          </div>
        )}

        <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-black text-slate-950">Preparar propiedades existentes</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Genera las señales del Asesor IA para las propiedades publicadas anteriormente.
              </p>
            </div>
            <button
              type="button"
              onClick={prepareExistingProperties}
              disabled={isPreparingSignals || !key.trim()}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-black text-orange-700 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
            >
              <RefreshCw size={16} className={isPreparingSignals ? "animate-spin" : ""} />
              {isPreparingSignals ? "Preparando..." : "Preparar ahora"}
            </button>
          </div>
          {signalMessage && (
            <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-800">
              {signalMessage}
            </p>
          )}
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Alertas</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{alerts.length}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Pro recientes</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{rows.length}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Estado</p>
            <p className="mt-1 flex items-center gap-2 text-sm font-black text-emerald-700">
              <CheckCircle2 size={16} />
              Panel activo
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-black text-slate-950">Casos que revisar</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {alerts.length === 0 && (
              <div className="p-5 text-sm text-slate-600">
                No hay alertas Pro pendientes.
              </div>
            )}

            {alerts.map((item) => (
              <article key={item.id} className="p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="flex items-center gap-2 font-black text-slate-950">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      {item.ubicacion || "Sin ubicacion"}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {item.nombre} · {item.telefono} · {item.correo}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-red-700">
                      {item.analisis_error || "Analisis pendiente."}
                    </p>
                  </div>
                  <div className="text-sm font-bold text-slate-500">
                    {item.analisis_estado}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Consultas del asesor</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{advisorStats.total || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Compatibilidad promedio</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{advisorStats.averageScore || 0}%</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-bold uppercase text-slate-500">Reglas fuertes activadas</p>
            <p className="mt-1 text-2xl font-black text-slate-950">{advisorStats.hardRuleCases || 0}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-[280px_1fr]">
          <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-black text-slate-950">Giros mas consultados</h2>
            <div className="mt-3 space-y-2">
              {(advisorStats.topBusinesses || []).map((item) => (
                <div key={item.business} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="capitalize text-slate-700">{item.business}</span>
                  <span className="font-black text-slate-950">{item.total}</span>
                </div>
              ))}
              {!advisorStats.topBusinesses?.length && (
                <p className="text-sm text-slate-500">Todavia no hay consultas.</p>
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="font-black text-slate-950">Historial del Asesor IA</h2>
            </div>
            <div className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto">
              {advisorLogs.map((item) => (
                <article key={item.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-black capitalize text-slate-950">{item.business_query}</p>
                      <p className="mt-1 text-xs text-slate-500">{item.property_public_id}</p>
                      {item.negative_reasons?.[0] && (
                        <p className="mt-2 text-sm text-amber-800">{item.negative_reasons[0]}</p>
                      )}
                    </div>
                    <div className="rounded-lg bg-blue-50 px-3 py-2 text-center text-blue-800">
                      <p className="text-lg font-black">{Math.round(Number(item.score || 0))}%</p>
                      <p className="text-[9px] font-bold uppercase">{item.status}</p>
                    </div>
                  </div>
                </article>
              ))}
              {!advisorLogs.length && (
                <p className="p-5 text-sm text-slate-500">Todavia no hay historial.</p>
              )}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default AdminProAlertas;
